import React from 'react';
import { theme } from 'antd';
import { CORAL } from '../theme/creamyTokens';

export interface ISegmentedPillOption<T extends string> {
  value: T;
  label: string;
  /** Sits to the right of the label -- an unseen count, a warning dot. */
  badge?: React.ReactNode;
}

export interface ISegmentedPillToggleProps<T extends string> {
  options: ISegmentedPillOption<T>[];
  value: T;
  onChange: (value: T) => void;
  style?: React.CSSProperties;
}

/**
 * Two or three views of the same screen, as a filled pill inside a track:
 * "Mis reservas / Entrantes", "Próximas / Pasadas".
 *
 * Built for the phone and, like most things here, gated to it -- desktop got
 * antd's underlined Tabs, or in the case of the guest/host switch, nothing at
 * all: the two lists were two separate top-level nav entries there, which is
 * why the header had nine of them.
 *
 * Nothing about it was ever mobile-specific, so it is shared now and both
 * sizes get the same control.
 */
function SegmentedPillToggle<T extends string>({ options, value, onChange, style }: ISegmentedPillToggleProps<T>): React.ReactElement {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: token.colorFillTertiary,
        marginBottom: 20,
        ...style,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              border: 'none',
              borderRadius: 999,
              padding: '8px 0',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? CORAL : 'transparent',
              color: active ? token.colorTextLightSolid : token.colorTextSecondary,
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
          >
            {option.label}
            {option.badge}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedPillToggle;
